/**
 * Pure mapping between the marketing-automation API resource and the
 * SectionsBuilder form state.
 *
 * The automation resource returns `email_template.id` as a hash_id while
 * creates/updates and the template picker use uuids; `email_template.uuid`
 * bridges that. Payloads predating the field fall back to a (name, subject)
 * match against the picker list; unresolved sections keep the name for
 * display and must be re-picked before saving.
 */
import {
  AutomationComparison,
  AutomationConditionType,
  AutomationSectionPayload,
  EmailTemplateModel,
  MarketingAutomationModel,
} from '@/typings/api/emailCenter';

export interface FormCondition {
  type: AutomationConditionType;
  attribute?: string;
  comparison?: AutomationComparison;
  value?: string;
}

export interface FormSection {
  /** Stable client-side key for drag & drop — never sent to the API. */
  uid: string;
  template_id?: string; // uuid
  unresolvedTemplateName?: string;
  wait_in_days: number;
  conditions: FormCondition[];
}

export const VALUELESS_COMPARISONS: AutomationComparison[] = [
  'is_empty',
  'is_not_empty',
];

let uidCounter = 0;
const nextUid = () => `section-${++uidCounter}`;

export function createEmptySection(): FormSection {
  return { uid: nextUid(), wait_in_days: 0, conditions: [] };
}

export function createEmptyCondition(): FormCondition {
  return { type: 'attribute', comparison: '=' };
}

export function resourceToFormSections(
  automation: MarketingAutomationModel,
  pickerTemplates: EmailTemplateModel[],
): FormSection[] {
  return (automation.sections || []).map((section) => {
    const resolved = section.email_template
      ? pickerTemplates.find(
          (template) => template.id === section.email_template!.uuid,
        ) ??
        pickerTemplates.find(
          (template) =>
            template.name === section.email_template!.name &&
            template.subject === section.email_template!.subject,
        )
      : undefined;

    return {
      uid: nextUid(),
      template_id: resolved?.id,
      unresolvedTemplateName:
        !resolved && section.email_template
          ? section.email_template.name
          : undefined,
      wait_in_days: section.wait_in_days ?? 0,
      conditions: (section.conditions || []).map((condition) => ({
        type: condition.type,
        attribute: condition.attribute,
        comparison: condition.comparison,
        value: condition.value != null ? String(condition.value) : undefined,
      })),
    };
  });
}

function cleanCondition(condition: FormCondition): FormCondition {
  if (condition.type === 'attribute') {
    const cleaned: FormCondition = {
      type: condition.type,
      attribute: condition.attribute,
      comparison: condition.comparison,
    };
    if (
      condition.comparison &&
      !VALUELESS_COMPARISONS.includes(condition.comparison)
    ) {
      cleaned.value = condition.value;
    }
    return cleaned;
  }
  // has_tag / has_not_tag: the tag itself travels as value.
  return { type: condition.type, value: condition.value };
}

export function sectionsToPayload(
  sections: FormSection[],
): AutomationSectionPayload[] {
  return sections.map((section) => ({
    template_id: section.template_id!,
    wait_in_days: Math.max(0, Number(section.wait_in_days) || 0),
    conditions: section.conditions.map(cleanCondition),
  }));
}

/** True when every section has a template and a non-negative wait. */
export function areSectionsValid(sections: FormSection[]): boolean {
  return (
    sections.length > 0 &&
    sections.every(
      (section) =>
        Boolean(section.template_id) &&
        Number.isFinite(Number(section.wait_in_days)) &&
        Number(section.wait_in_days) >= 0,
    )
  );
}

export function reorderSections(
  sections: FormSection[],
  activeUid: string,
  overUid: string,
): FormSection[] {
  const fromIndex = sections.findIndex((section) => section.uid === activeUid);
  const toIndex = sections.findIndex((section) => section.uid === overUid);
  if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex)
    return sections;

  const next = [...sections];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}
